import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, History, Loader, X, XCircle } from 'lucide-react';
import { modpackConfigsApiService } from '../../services/modpackConfigsApi';
import type { ClusterWipeResponse, ModpackConfig, ModpackConfigEnv } from '../../types/modpackConfig';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { lastWipe, localInputToSeconds, secondsToLocalInput } from './configFields';
import { formatWipe, wipeAgo } from './ModpackConfigBadges';
import { cn } from '../../utils/cn';

interface RecordWipeModalProps {
  config: ModpackConfig;
  /** Every server the wipe applies to — the cluster, or just this one. */
  members: ModpackConfig[];
  env: ModpackConfigEnv;
  onClose: () => void;
  onRecorded: () => Promise<void> | void;
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

/**
 * Recording a world wipe.
 *
 * A wipe is done to a pack, not to one region of it, so it is recorded on the
 * whole cluster at once — the same shape as a version bump. The date defaults
 * to now, because the usual moment to record a wipe is just after doing it,
 * but can be set back for one that was done earlier and never written down.
 * The date is what players are shown as "last wiped".
 */
export const RecordWipeModal = ({ config, members, env, onClose, onRecorded }: RecordWipeModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const nowSeconds = useMemo(() => Math.floor(Date.now() / 1000), []);
  const [when, setWhen] = useState<string>(secondsToLocalInput(nowSeconds));
  const [phase, setPhase] = useState<'form' | 'saving' | 'done'>('form');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ClusterWipeResponse | null>(null);

  const seconds = localInputToSeconds(when);
  const inFuture = seconds !== null && seconds > nowSeconds + 60 * 60;
  const canSubmit = seconds !== null && !inFuture && phase === 'form';

  const previous = useMemo(() => {
    const stamps = [...new Set(members.map((m) => lastWipe(m)))];
    return stamps;
  }, [members]);

  const handleSubmit = async () => {
    if (!canSubmit || seconds === null) return;
    setError(null);
    setPhase('saving');
    try {
      let outcome: ClusterWipeResponse;
      if (config.cluster_parent === null) {
        // A pack with no cluster row is still a pack: record it on the one
        // server rather than refusing.
        await modpackConfigsApiService.updateConfig(config.server_address, { date_wiped: seconds }, env);
        outcome = {
          success: true, cluster_parent: config.id, date_wiped: seconds,
          updated: [config.server_address], failed: [],
        };
      } else {
        outcome = await modpackConfigsApiService.recordClusterWipe(config.cluster_parent, seconds, env);
      }
      setResult(outcome);
      await onRecorded();
      setPhase('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The wipe could not be recorded');
      setPhase('form');
    }
  };

  const busy = phase === 'saving';
  const name = config.cluster_name && config.cluster_name !== 'default'
    ? config.cluster_name
    : (config.modpack_full_name || config.server_address);

  return createPortal(
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className="modal-panel max-w-xl max-md:max-w-[95vw]" role="dialog" aria-modal="true">
        <div className="modal-header">
          <div className="min-w-0">
            <h2 className="modal-title">Record a world wipe — {name}</h2>
            <p className="modal-subtitle">
              {members.length === 1
                ? 'Sets the last-wipe date on this server. Players are shown it.'
                : `Sets the last-wipe date on all ${members.length} servers. Players are shown it.`}
            </p>
          </div>
          <button type="button" onClick={onClose} className="modal-close" aria-label="Close" disabled={busy}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body space-y-4">
          {phase !== 'done' && (
            <>
              {error && (
                <div className="alert alert-danger" role="alert">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label htmlFor="wipe-when" className={LABEL_CLASS}>When was the world wiped?</label>
                <input
                  id="wipe-when"
                  type="datetime-local"
                  value={when}
                  max={secondsToLocalInput(nowSeconds + 60 * 60)}
                  onChange={(e) => setWhen(e.target.value)}
                  className={cn('glass-input w-full', inFuture && 'border-red-500/40')}
                  disabled={busy}
                />
                <p className="text-xs text-glass-500 mt-1">
                  {inFuture
                    ? 'That is in the future — a wipe is recorded after it happens.'
                    : seconds === null
                      ? 'Pick the date and time of the wipe. Just now is the usual answer.'
                      : `Players will see "last wiped ${wipeAgo(seconds)}" (${formatWipe(seconds)}).`}
                </p>
              </div>

              <div className="subcard p-3">
                <p className="text-xs font-medium text-glass-400 mb-2">Recorded on</p>
                <ul className="divide-y divide-glass-200/10">
                  {members.map((m) => (
                    <li key={m.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <span className="font-mono text-white truncate">{m.server_address}</span>
                      <span className="text-xs text-glass-400 shrink-0">
                        {lastWipe(m) === null ? 'never wiped' : `last ${wipeAgo(lastWipe(m) as number)}`}
                      </span>
                    </li>
                  ))}
                </ul>
                {previous.length > 1 && (
                  <p className="text-xs text-amber-300 mt-2">
                    These servers currently disagree about their last wipe; this records one date on all of them.
                  </p>
                )}
              </div>
            </>
          )}

          {phase === 'done' && result && (
            <div className="space-y-3">
              <div className={cn('alert', result.success ? 'alert-success' : 'alert-danger')} role="status">
                {result.success ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
                <span>
                  {result.success
                    ? `Recorded: wiped ${wipeAgo(result.date_wiped)} (${formatWipe(result.date_wiped)}) on ${result.updated.length} server${result.updated.length === 1 ? '' : 's'}.`
                    : `${result.failed.length} of ${members.length} servers did not take the date. Fix these and record again; re-running is safe.`}
                </span>
              </div>
              {result.failed.length > 0 && (
                <ul className="subcard p-3 divide-y divide-glass-200/10">
                  {result.failed.map((f) => (
                    <li key={f.server_address} className="py-2 text-sm">
                      <span className="font-mono block">{f.server_address}</span>
                      <span className="text-xs text-glass-400 break-all">{f.error}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {phase === 'done' ? (
            <button type="button" onClick={onClose} className="btn-primary ml-auto">Done</button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="glass-button-secondary" disabled={busy}>Cancel</button>
              <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="btn-primary">
                {busy ? <Loader className="w-4 h-4 animate-spin" /> : <History className="w-4 h-4" />}
                {busy ? 'Recording…' : 'Record wipe'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
